import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  CreditCard,
  RefreshCw,
  Eye,
  MoreHorizontal,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/DataTable'
import { Transaction } from '@/types'
import { dashboardService } from '@/services/dashboardService'
import { formatDistanceToNow } from 'date-fns'
import { TableColumn, TableAction, TablePagination, TableSort, TableFilter } from '@/types/table'

type ApiResponse<T> = {
  success: boolean
  message: string
  data: {
    results: T[]
    count: number
    next: string | null
    previous: string | null
  }
}

const statusConfig = {
  pending: {
    label: 'Pending',
    color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    icon: Clock,
  },
  processing: {
    label: 'Processing',
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    icon: RefreshCw,
  },
  completed: {
    label: 'Completed',
    color: 'text-green-600 bg-green-50 border-green-200',
    icon: CheckCircle,
  },
  failed: {
    label: 'Failed',
    color: 'text-orange-600 bg-orange-50 border-orange-200',
    icon: XCircle,
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-gray-600 bg-gray-50 border-gray-200',
    icon: XCircle,
  },
  refunded: {
    label: 'Refunded',
    color: 'text-purple-600 bg-purple-50 border-purple-200',
    icon: AlertCircle,
  },
}

const paymentMethodConfig = {
  card: { label: 'Card', icon: CreditCard },
  bank_transfer: { label: 'Bank Transfer', icon: TrendingUp },
  mobile_money: { label: 'Mobile Money', icon: Users },
  crypto: { label: 'Crypto', icon: DollarSign },
  other: { label: 'Other', icon: MoreHorizontal },
}

export default function Transactions() {
  const navigate = useNavigate()
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [pagination, setPagination] = useState<TablePagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [5, 10, 20, 50],
  })
  const [sort, setSort] = useState<TableSort | undefined>({
    key: 'created_at',
    direction: 'desc',
  })
  const [filters, setFilters] = useState<TableFilter[]>([])

  const fetchTransactions = async (params: {
    page: number
    pageSize: number
    sort?: TableSort
    filters: TableFilter[]
    search?: string
  }) => {
    setLoading(true)
    setError(null)
    try {
      // Convert filters to API parameters
      const statusFilter = filters.find(f => f.key === 'status')
      const startDateFilter = filters.find(f => f.key === 'start_date')
      const endDateFilter = filters.find(f => f.key === 'end_date')

      const response: ApiResponse<Transaction> = await dashboardService.getTransactions({
        page: params.page,
        limit: params.pageSize,
        status: statusFilter?.value || undefined,
        start_date: startDateFilter?.value || undefined,
        end_date: endDateFilter?.value || undefined,
        // search parameter is handled by the backend filtering
      })

      if (response.success && response.data) {
        const { results = [], count = 0 } = response.data

        setTransactions(results)
        setTotalCount(count)
        setPagination(prev => ({
          ...prev,
          current: params.page,
          total: count,
        }))
        return { data: results, total: count }
      } else {
        const errorMessage = response.message || 'Failed to fetch transactions'
        setError(errorMessage)
        return { data: [], total: 0 }
      }
    } catch (error: any) {
      let errorMessage = 'Failed to fetch transactions'

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      console.error('Failed to fetch transactions:', {
        error,
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
      })

      setError(errorMessage)
      setTransactions([])
      setTotalCount(0)
      setPagination(prev => ({ ...prev, total: 0 }))

      return { data: [], total: 0 }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTransactions({
      page: pagination.current,
      pageSize: pagination.pageSize,
      sort,
      filters,
      search: '',
    })
  }, [])

  const getStatusConfig = (status: string) => {
    return statusConfig[status as keyof typeof statusConfig] || statusConfig.pending
  }

  const getPaymentMethodConfig = (method: string) => {
    return (
      paymentMethodConfig[method as keyof typeof paymentMethodConfig] || paymentMethodConfig.other
    )
  }

  const formatAmount = (amount: string) => {
    return `$${parseFloat(amount).toFixed(2)}`
  }

  // Define table columns
  const columns: TableColumn<Transaction>[] = [
    {
      key: 'transaction_reference',
      title: 'Transaction',
      dataIndex: 'transaction_reference',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div>
          <p
            className="font-medium"
            style={pageStyles.text}
          >
            {record.transaction_reference}
          </p>
          {record.external_reference && (
            <p
              className="text-sm"
              style={pageStyles.textSecondary}
            >
              {record.external_reference}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'customer',
      title: 'Customer',
      dataIndex: 'full_name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div>
          <p
            className="font-medium"
            style={pageStyles.text}
          >
            {record.full_name || 'N/A'}
          </p>
          <p
            className="text-sm"
            style={pageStyles.textSecondary}
          >
            {record.email || record.phone || 'N/A'}
          </p>
        </div>
      ),
    },
    {
      key: 'total_amount',
      title: 'Amount',
      dataIndex: 'total_amount',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: value => (
        <p
          className="font-semibold"
          style={pageStyles.text}
        >
          {formatAmount(value)}
        </p>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: Object.entries(statusConfig).map(([key, config]) => ({
        label: config.label,
        value: key,
      })),
      render: value => {
        const config = getStatusConfig(value)
        const StatusIcon = config.icon
        return (
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}
          >
            <StatusIcon className="w-3 h-3 mr-1" />
            {config.label}
          </span>
        )
      },
    },
    {
      key: 'payment_method',
      title: 'Payment Method',
      dataIndex: 'payment_method',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: Object.entries(paymentMethodConfig).map(([key, config]) => ({
        label: config.label,
        value: key,
      })),
      render: value => {
        const config = getPaymentMethodConfig(value || 'other')
        const PaymentIcon = config.icon
        return (
          <div className="flex items-center">
            <PaymentIcon
              className="w-4 h-4 mr-2"
              style={{ color: branding.textSecondaryColor }}
            />
            <span
              className="text-sm"
              style={pageStyles.text}
            >
              {config.label}
            </span>
          </div>
        )
      },
    },
    {
      key: 'created_at',
      title: 'Date',
      dataIndex: 'created_at',
      sortable: true,
      filterable: false,
      render: value => (
        <div>
          <p
            className="text-sm"
            style={pageStyles.text}
          >
            {new Date(value).toLocaleDateString()}
          </p>
          <p
            className="text-xs"
            style={pageStyles.textSecondary}
          >
            {formatDistanceToNow(new Date(value), { addSuffix: true })}
          </p>
        </div>
      ),
    },
  ]

  // Define table actions
  const actions: TableAction<Transaction>[] = [
    {
      key: 'view',
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: record => {
        navigate(`/transactions/${record.id}`)
      },
    }
  ]

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="relative z-10 p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Total Transactions
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {totalCount.toLocaleString()}
                  </p>
                  <p
                    className="mt-1 text-xs"
                    style={pageStyles.textSecondary}
                  >
                    All transactions
                  </p>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={pageStyles.primaryBg}
                >
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Completed
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {transactions.filter(t => t.status === 'completed').length}
                  </p>
                  <p
                    className="mt-1 text-xs"
                    style={pageStyles.textSecondary}
                  >
                    Successfully processed
                  </p>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={{ backgroundColor: branding.secondaryColor }}
                >
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Pending
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {transactions.filter(t => t.status === 'pending').length}
                  </p>
                  <p
                    className="mt-1 text-xs"
                    style={pageStyles.textSecondary}
                  >
                    Awaiting processing
                  </p>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={pageStyles.primaryBg}
                >
                  <Clock className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Failed
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {transactions.filter(t => t.status === 'failed').length}
                  </p>
                  <p
                    className="mt-1 text-xs"
                    style={pageStyles.textSecondary}
                  >
                    Failed transactions
                  </p>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={{ backgroundColor: branding.secondaryColor }}
                >
                  <XCircle className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div
            className="p-6 mb-8 transition-all duration-300 border card backdrop-blur-sm"
            style={{
              ...cardStyles,
              borderColor: statusColors.error.borderColor
            }}
          >
            <div className="flex items-start space-x-4">
              <div
                className="p-3 rounded-xl"
                style={{
                  backgroundColor: statusColors.error.backgroundColor
                }}
              >
                <XCircle
                  className="w-6 h-6"
                  style={{ color: statusColors.error.color }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  className="mb-1 text-lg font-semibold"
                  style={pageStyles.text}
                >
                  Error loading transactions
                </h3>
                <p
                  className="mb-4 text-sm"
                  style={{ color: statusColors.error.color }}
                >
                  {error}
                </p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      fetchTransactions({
                        page: 1,
                        pageSize: pagination.pageSize,
                        sort,
                        filters,
                        search: '',
                      })
                    }
                    style={{
                      backgroundColor: `${statusColors.error.color}20`,
                      borderColor: statusColors.error.borderColor,
                      color: statusColors.error.color
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    style={{
                      color: statusColors.error.color
                    }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div
          className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
          style={cardStyles}
        >
          <DataTable
            columns={columns}
            data={transactions}
            loading={loading}
            pagination={pagination}
            filters={filters}
            sort={sort}
            actions={actions}
            rowKey="id"
            onSort={setSort}
            onFilter={setFilters}
            onPageChange={(page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }))
              fetchTransactions({
                page,
                pageSize,
                sort,
                filters,
                search: '',
              })
            }}
            onSearch={search => {
              fetchTransactions({
                page: 1,
                pageSize: pagination.pageSize,
                sort,
                filters,
                search,
              })
            }}
            onServerSideChange={fetchTransactions}
            serverSide={true}
            searchable={true}
            searchPlaceholder="Search transactions..."
            emptyText="No transactions found"
            emptyIcon={<CreditCard className="w-12 h-12" style={{ color: branding.textSecondaryColor }} />}
            loadingText="Loading transactions..."
            showStats={true}
            showSearch={true}
            showFilters={true}
            showPagination={true}
            showActions={true}
            striped={true}
            hoverable={true}
            bordered={false}
            className="!bg-transparent !border-none !rounded-none"
          />
        </div>
      </div>
    </div>
  )
}
